import { Controller, useFormContext } from 'react-hook-form'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material'
import { AddressForm, MaskedInput } from '@/components'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { useReadPartner } from '@/api/accounting-dealer/company'
import { useOutletContext } from 'react-router-dom'
import { ReadDealResponse } from '@/types'
import { CompanyFormData } from './schema'
import { useReadCompanySerpro } from '@/api/accounting-dealer/company'
import { CNPJ_IMASK_OPTS, removeNonNumeric, sanitizeCnpj } from '@/helpers'
import { readZipcode } from '@/api/accounting-dealer/person'

type CompanyFormProps = {
  onSubmit: (data: CompanyFormData) => void
  isChangingAccountant: boolean
  mainPartnerKey?: string
}

export const CompanyForm = forwardRef<HTMLFormElement, CompanyFormProps>(
  ({ onSubmit, isChangingAccountant, mainPartnerKey }, ref) => {
    const { deal } = useOutletContext<ReadDealResponse>()
    const { control, setValue, handleSubmit, watch } =
      useFormContext<CompanyFormData>()

    const taxId = watch('tax_id') ?? ''
    const cleanTaxId = sanitizeCnpj(taxId)
    const isTaxIdComplete = cleanTaxId.length === 14

    const previousTaxId = useRef<string>('')
    const [hasZipcodeLookupError, setHasZipcodeLookupError] = useState(false)

    const {
      data: serproData,
      isFetching: isLoadingSerpro,
      isError: hasSerproError,
    } =
      useReadCompanySerpro(
        { companyKey: deal.company.key, taxId: cleanTaxId },
        { enabled: isChangingAccountant && isTaxIdComplete },
      )
    const shouldDisableCompanyFields =
      isChangingAccountant &&
      !(isTaxIdComplete && (hasSerproError || hasZipcodeLookupError))

    useEffect(() => {
      setHasZipcodeLookupError(false)
    }, [cleanTaxId])

    useEffect(() => {
      if (!serproData || cleanTaxId === previousTaxId.current) return
      previousTaxId.current = cleanTaxId

      setValue('corporate_name', serproData.corporate_name, {
        shouldValidate: true,
      })
      if (serproData.address) {
        const addr = serproData.address
        setValue('address.zip_code', addr.zip_code, { shouldValidate: true })
        setValue('address.street', addr.street, { shouldValidate: true })
        setValue('address.number', addr.number, { shouldValidate: true })
        setValue('address.district', addr.district, { shouldValidate: true })
        setValue('address.ibge_code', addr.ibge_code, { shouldValidate: true })
        setValue('address.complement', addr.complement ?? '', {
          shouldValidate: true,
        })
        const stateCode = (addr as { state_code?: string }).state_code ?? ''
        setValue('address.state_code', stateCode, {
          shouldValidate: true,
        })

        // Serpro pode não retornar state_code. Nesse caso, resolve pelo CEP para
        // garantir state/city consistentes e disparar carregamento de municípios.
        if (!stateCode && addr.zip_code) {
          setHasZipcodeLookupError(false)
          readZipcode(removeNonNumeric(addr.zip_code))
            .then((cepData) => {
              setHasZipcodeLookupError(false)
              setValue('address.state_code', cepData.state_code, {
                shouldValidate: true,
              })
              setValue('address.ibge_code', cepData.ibge_code, {
                shouldValidate: true,
              })
            })
            .catch(() => {
              // Se falhar, mantém os dados preenchidos para edição manual.
              setHasZipcodeLookupError(true)
            })
        }
      }
    }, [serproData, cleanTaxId, setValue])

    const { data: mainPartner } = useReadPartner(
      { partnerKey: mainPartnerKey ?? '', companyKey: deal.company.key },
      { enabled: !isChangingAccountant && !!mainPartnerKey },
    )

    const applyMainPartnerAddress = () => {
      if (!mainPartner?.address) return
      const { address } = mainPartner
      setValue('address.state_code', address.state_code)
      setValue('address.number', address.number)
      setValue('address.zip_code', address.zip_code)
      setValue('address.street', address.street)
      setValue('address.district', address.district)
      setValue('address.complement', mainPartner.address.complement)
      setValue('address.ibge_code', address.ibge_code)
    }

    return (
      <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          {isChangingAccountant && (
            <Controller
              name="tax_id"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <MaskedInput
                  label="Qual o CNPJ da sua empresa?"
                  error={Boolean(error)}
                  helperText={error?.message}
                  mask={CNPJ_IMASK_OPTS}
                  InputProps={{
                    endAdornment: isLoadingSerpro ? (
                      <InputAdornment position="end">
                        <CircularProgress size={18} />
                      </InputAdornment>
                    ) : undefined,
                  }}
                  {...field}
                />
              )}
            />
          )}

          <Controller
            name="corporate_name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <MaskedInput
                label={isChangingAccountant ? "Confirme o nome da sua PJ" : "Qual é a Razão Social (nome) da PJ?"}
                error={Boolean(error)}
                helperText={error?.message}
                disabled={shouldDisableCompanyFields}
                {...field}
              />
            )}
          />

          {serproData?.simple_tax_regime && (
            <Box>
            <Chip
              label="Empresa optante pelo Simples Nacional"
              color="default"
            />
            </Box>
          )}

          <Box>
            <Typography variant="h6">
              {isChangingAccountant ? "Confirme o endereço fiscal da sua PJ" : "Qual será o endereço fiscal da sua PJ?"}
            </Typography>

            {!isChangingAccountant && (
              <Button
                variant="outlined"
                size="small"
                onClick={applyMainPartnerAddress}
                sx={{ my: 2 }}
              >
                Aplicar endereço do sócio principal
              </Button>
            )}
          </Box>

          <AddressForm disabled={shouldDisableCompanyFields} />
        </Stack>
      </form>
    )
  },
)
